import React, { useCallback } from 'react';
import styled from 'styled-components';
import { layout } from 'src/components/base';

import { useAppContext } from 'src/components/app-context';
import { trackProductCollectionWordCloudClick } from 'src/areas/product-collection/tracking';

import * as S from './styles';
import {
  TonalButton,
  ButtonText,
  ButtonIcon,
  ButtonSize,
} from '@prisjakt/ui/button/variants';
import { Clickable } from 'src/components/clickable';
import { IconMagnifier } from '@prisjakt/ui/icon/magnifier';
import { Behavior } from '@prisjakt/ui/text';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type WordCloudProps = {
  title: string;
  items: WordCloudItem[];
  showMoreUrl?: string;
  numberOfVisibleItems?: number;
};

type WordCloudItem = {
  name: string;
  url: string;
};

const WordCloud = ({
  title,
  items,
  showMoreUrl,
  numberOfVisibleItems,
}: WordCloudProps) => {
  const { t } = useAppContext();

  const onClickWord = useCallback((event: React.MouseEvent, index?: number) => {
    const href = event.currentTarget.getAttribute('href') as string;
    trackProductCollectionWordCloudClick(href);
    trackInternalNavigationClick({
      scope: InternalNavigationScope.RELATED_SEARCHES_WORD,
      nextPage: href,
      index,
    });
  }, []);

  const onClickShowMore = useCallback((event: React.MouseEvent) => {
    const href = event.currentTarget.getAttribute('href') as string;
    trackProductCollectionWordCloudClick(href);
    trackInternalNavigationClick({
      scope: InternalNavigationScope.RELATED_SEARCHES_SHOW_MORE,
      nextPage: href,
    });
  }, []);

  const wordCloudItems = numberOfVisibleItems
    ? items.slice(0, numberOfVisibleItems)
    : items;

  return (
    <S.WordCloudContainer>
      <S.WordCloudH3>{title}</S.WordCloudH3>

      <List>
        {wordCloudItems.map(({ name, url }, index) => (
          <ListItem key={url}>
            <TonalButton
              size={ButtonSize.SMALL}
              onClick={e => onClickWord(e, index)}
              container={Clickable}
              containerProps={{
                to: url,
                title: name,
              }}
              fullWidth
            >
              <ButtonIcon>
                <IconMagnifier />
              </ButtonIcon>
              <ButtonText behavior={Behavior.OnelineEllipsis}>
                {name}
              </ButtonText>
            </TonalButton>
          </ListItem>
        ))}

        {showMoreUrl && (
          <S.ShowMoreButton>
            <TonalButton
              container={Clickable}
              containerProps={{ to: showMoreUrl }}
              onClick={onClickShowMore}
              size={ButtonSize.SMALL}
            >
              <ButtonText>{t('common', 'button.show-more')}</ButtonText>
              <ButtonIcon>
                <S.ShowMoreIcon />
              </ButtonIcon>
            </TonalButton>
          </S.ShowMoreButton>
        )}
      </List>
    </S.WordCloudContainer>
  );
};

export const List = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
`;

const ListItem = styled.li`
  margin: ${layout.measurements.spacing025};
  overflow: hidden;
  max-width: 100%;
`;

export default WordCloud;
